import { create } from "zustand";

/**
 * Returns the next tab or step id (string) after the given currentTabId in the sidebar structure, or null if none exists.
 * Flattens all tabs and steps (including steps with no tabs/options) into a single sequence.
 */
function getNextTabId(sidebarData, currentTabId) {
  const flatTabs = [];

  sidebarData.forEach((step) => {
    if (step.tab) {
      step.tab.forEach((tab) => flatTabs.push(tab.id));
    }
    if (step.option) {
      step.option.forEach((opt) => {
        if (opt.tab) {
          opt.tab.forEach((tab) => flatTabs.push(tab.id));
        }
      });
    }
    if (!step.tab && !step.option && step.id) {
      flatTabs.push(step.id);
    }
  });

  const idx = flatTabs.indexOf(currentTabId);
  return idx >= 0 && idx + 1 < flatTabs.length ? flatTabs[idx + 1] : null;
}

/**
 * Unlocks the next locked tab or step in the sidebar data, following a strict sequential order.
 * If all tabs are already unlocked, unlocks the first eligible step with no tabs/options after all previous are complete.
 * Ensures only one new item is unlocked per call, supporting wizard-like progression.
 * Returns a new sidebar data array with updated access flags.
 */
const unlockNextItem = (data) => {
  const cloned = structuredClone(data);

  // Flatten all tab items with their context (including access: false)
  const flatTabs = [];
  cloned.forEach((step, stepIdx) => {
    if (step.tab) {
      step.tab.forEach((tab, tabIdx) => {
        flatTabs.push({ tab, stepIdx, tabIdx, optionIdx: null });
      });
    }
    if (step.option) {
      step.option.forEach((opt, optionIdx) => {
        if (opt.tab) {
          opt.tab.forEach((tab, tabIdx) => {
            flatTabs.push({ tab, stepIdx, optionIdx, tabIdx });
          });
        }
      });
    }
  });

  // Find the last accessed tab (the last one with access: true)
  const lastAccessedIndex = flatTabs
    .map((item) => item.tab.access)
    .lastIndexOf(true);

  // Unlock the next tab or step in sequence (if any), or the first locked item after all previous are unlocked
  let unlocked = false;
  for (let i = 0; i < flatTabs.length; i++) {
    const item = flatTabs[i];
    // If this is the first locked item after all previous are unlocked, unlock it
    if (!item.tab.access && !unlocked) {
      if (item.optionIdx === null) {
        cloned[item.stepIdx].tab[item.tabIdx].access = true;
        cloned[item.stepIdx].access = true;
      } else {
        cloned[item.stepIdx].option[item.optionIdx].tab[item.tabIdx].access =
          true;
        cloned[item.stepIdx].option[item.optionIdx].access = true;
        cloned[item.stepIdx].access = true;
      }
      unlocked = true;
    }
  }
  // If all tabs are unlocked, unlock the first locked step with no tabs/options after all previous are complete
  if (!unlocked) {
    for (let i = 0; i < cloned.length; i++) {
      const step = cloned[i];
      const hasTabOrOpt =
        (step.tab && step.tab.length) || (step.option && step.option.length);
      if (!step.access && !hasTabOrOpt) {
        const prevComplete = cloned.slice(0, i).every((prev) => {
          const tAccess = prev.tab ? prev.tab.every((t) => t.access) : true;
          const oAccess = prev.option
            ? prev.option.every((o) =>
                o.tab ? o.tab.every((t) => t.access) : true,
              )
            : true;
          const sAccess = !prev.tab && !prev.option ? prev.access : true;
          return tAccess && oAccess && sAccess;
        });
        if (prevComplete) {
          step.access = true;
          break;
        }
      }
    }
  }

  // Removed loop that unlocked the next step with no tabs/options after the +1 logic. Now, only the +1 logic above will unlock the next tab or step.

  return cloned;
};

/**
 * Zustand store for sidebar navigation state and unlocking logic.
 * - sidebarData: The current sidebar structure and access state.
 * - setSidebarData: Sets sidebar data and applies unlocking logic.
 * - unlockTabById: Unlocks all tabs up to and including a given tab id.
 * - getNextTabId: Returns the id of the next tab/step in sequence after a given tab id.
 */
const useSidebarStore = create((set) => ({
  sidebarData: [],
  setSidebarData: (data) => {
    const updated = unlockNextItem(data);
    set({ sidebarData: updated });
  },
  unlockTabById: (tabId) => {
    set((state) => {
      const unlockUpTo = (arr) => {
        let found = false;
        return arr.map((t) => {
          if (!found) {
            const unlocked = { ...t, access: true };
            if (t.id === tabId) found = true;
            return unlocked;
          }
          return t;
        });
      };
      const updated = state.sidebarData.map((section) => {
        // Unlock tab in section.tab
        if (section.tab && section.tab.some((t) => t.id === tabId)) {
          section.tab = unlockUpTo(section.tab);
        }
        // Unlock tab in section.option[].tab
        if (section.option) {
          section.option = section.option.map((opt) => {
            if (opt.tab && opt.tab.some((t) => t.id === tabId)) {
              opt.tab = unlockUpTo(opt.tab);
            }
            return opt;
          });
        }
        // Unlock step directly if it has no tab/option and id matches
        if (!section.tab && !section.option && section.id === tabId) {
          return { ...section, access: true };
        }
        return section;
      });
      return { sidebarData: updated };
    });
  },
  getNextTabId: (currentTabId) => {
    const state = useSidebarStore.getState();
    return getNextTabId(state.sidebarData, currentTabId);
  },
}));

export default useSidebarStore;
